import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Check, ChevronDown, Loader2, X, XCircle } from 'lucide-react'
import type {
  AgentActionApplyOutcome,
  AgentActionRejectOutcome,
  InFlightAgentActionProposal
} from '@axilon-apps/domains-ontology-ui-state'
import {
  getAgentActionLifecycle,
  markAgentActionSettled,
  maybeFireAgentActionPlanResume,
  removeInFlightAgentAction,
  useAppDispatch
} from '@axilon-apps/domains-ontology-ui-state'
import { cn } from '../../lib/utils'
import { getAgentActionView } from './views/viewRegistry'
// Cypher cards are shared by query sidebar + ingest Conversation.
// Query-only action views (ServiceNow, equipment, …) register from the
// sidebar host so ingest does not pull those modules.
import './views/cypherWriteView'
import './views/graphWriteView'
import './views/customAgentConfigUpdateView'
import './views/customAgentRunView'
import './views/sightingsWriteView'

export interface AgentActionSettlement {
  toolUseId: string
  proposal: InFlightAgentActionProposal
  terminal: NonNullable<InFlightAgentActionProposal['terminalState']>
  settledMeta?: Record<string, unknown>
}

export interface AgentActionApprovalPanelProps {
  proposals: Record<string, InFlightAgentActionProposal>
  /**
   * Active conversation id, used to fire the synthetic follow-up agent
   * turn once every row in a `plan_id` reaches a terminal state (when
   * `firePlanResume` is true).
   */
  conversationId: string | null
  /**
   * Optional Neo4j branch for graph-affecting apply (ingest job branch).
   * Query chat relies on the global branch header instead.
   */
  branchId?: string
  /**
   * When true (default), settled plans kick the orchestrator resume thunk.
   * Ingest sets false — park resume is owned by Apply/Reject API routes.
   */
  firePlanResume?: boolean
  /**
   * Host-owned settlement hook (e.g. ingest local proposal map). Always
   * invoked after a successful apply/reject; Redux mark still runs so the
   * query sidebar stays consistent.
   */
  onSettled?: (settlement: AgentActionSettlement) => void
  /**
   * Host cleanup when the operator dismisses settled cards (ingest local map).
   * Query sidebar omits this and relies on Redux remove / plan-resume.
   */
  onRemoveSettled?: (toolUseIds: string[]) => void
  /**
   * When set, Apply / Approve all are disabled and this message is shown
   * (e.g. ingest waiting for the job branch). Reject stays available.
   */
  applyBlockedReason?: string | null
  /** Effective permissions for capability-specific Apply gating. */
  permissions?: readonly string[]
  /** Optional layout class (ingest mounts mid-column without sidebar chrome). */
  className?: string
}

type BulkOpState =
  | { kind: 'applying'; total: number; completed: number }
  | { kind: 'rejecting'; total: number; completed: number }
  | null

const BULK_REJECT_REASON = 'rejected by user (bulk)'
const SINGLE_REJECT_REASON = 'rejected by user'

export function agentActionPermissionBlockedReason(
  proposal: InFlightAgentActionProposal,
  permissions: readonly string[] | undefined
): string | null {
  const view = getAgentActionView(proposal.kind)
  if (view?.applyBlockedReason) return view.applyBlockedReason(proposal, permissions)
  const required = view?.requiredApplyPermission
  if (!required || permissions?.includes(required)) return null
  return view.permissionDeniedMessage ?? `Apply requires the ${required} permission.`
}

/**
 * Memoized so the per-token SSE re-renders of the chat sidebar don't
 * re-render the whole card list — `proposals` is an Immer-stable
 * reference that only changes identity when a proposal is actually
 * added / hydrated / settled.
 */
export const AgentActionApprovalPanel = memo(function AgentActionApprovalPanel({
  proposals,
  conversationId,
  branchId,
  firePlanResume = true,
  onSettled,
  onRemoveSettled,
  applyBlockedReason = null,
  permissions,
  className
}: AgentActionApprovalPanelProps) {
  const entries = useMemo(
    () =>
      Object.entries(proposals)
        .map(([toolUseId, proposal]) => ({ toolUseId, proposal }))
        .sort((a, b) => {
          if (a.proposal.startedAtMs !== b.proposal.startedAtMs) {
            return a.proposal.startedAtMs - b.proposal.startedAtMs
          }
          return (a.proposal.batchIndex ?? 0) - (b.proposal.batchIndex ?? 0)
        }),
    [proposals]
  )

  const dispatch = useAppDispatch()
  const [bulkOp, setBulkOp] = useState<BulkOpState>(null)

  const kickPlanResume = useCallback(
    (planId: string | undefined) => {
      if (!firePlanResume || !planId || !conversationId) return
      void dispatch(maybeFireAgentActionPlanResume(planId, conversationId))
    },
    [conversationId, dispatch, firePlanResume]
  )

  const performApply = useCallback(
    async (
      toolUseId: string,
      proposal: InFlightAgentActionProposal
    ): Promise<AgentActionApplyOutcome> => {
      const capability = getAgentActionLifecycle(proposal.kind)
      if (!capability) {
        return {
          kind: 'transport_error',
          error: `No handler for "${proposal.kind}".`
        }
      }
      const outcome = await dispatch(
        capability.apply(toolUseId, branchId ? { branchId } : undefined)
      )
      if (outcome.kind === 'success') {
        const terminal = { kind: 'applied' as const }
        dispatch(
          markAgentActionSettled({
            id: toolUseId,
            terminal,
            settledMeta: outcome.settledMeta
          })
        )
        onSettled?.({
          toolUseId,
          proposal,
          terminal,
          settledMeta: outcome.settledMeta
        })
        if (proposal.planId) {
          kickPlanResume(proposal.planId)
        } else if (firePlanResume) {
          // Defensive fallback for rows that never got their planId
          // hydrated (e.g. SSE never delivered `tool_call_complete`).
          dispatch(removeInFlightAgentAction({ id: toolUseId }))
        }
      } else if (outcome.kind === 'apply_failed') {
        const terminal = {
          kind: 'apply_failed' as const,
          error: outcome.error
        }
        dispatch(
          markAgentActionSettled({
            id: toolUseId,
            terminal
          })
        )
        onSettled?.({ toolUseId, proposal, terminal })
        // A plan-wide refusal (e.g. branch protection) fails every sibling
        // identically — settle them all so the plan reaches terminal state
        // and the resume turn relays the failure to the agent, instead of
        // leaving undecided cards stuck in the sidebar. The server rows are
        // still `awaiting_user` (the guard refused before the executor ran),
        // so each is parked in a terminal state via the reject endpoint —
        // otherwise the next pending-hydrate would resurrect the cards.
        if (outcome.planWide && proposal.planId) {
          void dispatch(capability.reject(toolUseId, outcome.error))
          for (const [siblingId, sibling] of Object.entries(proposals)) {
            if (siblingId === toolUseId) continue
            if (sibling.planId !== proposal.planId) continue
            if (sibling.terminalState !== undefined) continue
            const siblingTerminal = {
              kind: 'apply_failed' as const,
              error: outcome.error
            }
            dispatch(
              markAgentActionSettled({
                id: siblingId,
                terminal: siblingTerminal
              })
            )
            onSettled?.({
              toolUseId: siblingId,
              proposal: sibling,
              terminal: siblingTerminal
            })
            const siblingCapability = getAgentActionLifecycle(sibling.kind)
            if (siblingCapability) {
              void dispatch(siblingCapability.reject(siblingId, outcome.error))
            }
          }
        }
        kickPlanResume(proposal.planId)
      }
      // transport_error: leave the row actionable so the user can retry.
      return outcome
    },
    [branchId, dispatch, firePlanResume, kickPlanResume, onSettled, proposals]
  )

  const performReject = useCallback(
    async (
      toolUseId: string,
      proposal: InFlightAgentActionProposal,
      reason: string
    ): Promise<AgentActionRejectOutcome> => {
      const capability = getAgentActionLifecycle(proposal.kind)
      if (!capability) {
        return {
          kind: 'transport_error',
          error: `No handler for "${proposal.kind}".`
        }
      }
      const outcome = await dispatch(capability.reject(toolUseId, reason))
      if (outcome.kind === 'success') {
        const terminal = { kind: 'rejected' as const }
        dispatch(
          markAgentActionSettled({
            id: toolUseId,
            terminal,
            settledMeta: outcome.settledMeta
          })
        )
        onSettled?.({
          toolUseId,
          proposal,
          terminal,
          settledMeta: outcome.settledMeta
        })
        if (proposal.planId) {
          kickPlanResume(proposal.planId)
        } else if (firePlanResume) {
          dispatch(removeInFlightAgentAction({ id: toolUseId }))
        }
      }
      return outcome
    },
    [dispatch, firePlanResume, kickPlanResume, onSettled]
  )

  const pendingEntries = useMemo(
    () => entries.filter((e) => e.proposal.terminalState === undefined),
    [entries]
  )
  const permissionBlockedReason = useCallback(
    (proposal: InFlightAgentActionProposal): string | null =>
      agentActionPermissionBlockedReason(proposal, permissions),
    [permissions]
  )

  // Sequential apply keeps the per-card transitions readable (each row
  // flips from idle to settled in order) and avoids hammering the backing
  // store with concurrent writes that may share locks. The synthetic
  // resume turn only fires after the *last* successful apply because
  // `maybeFireAgentActionPlanResume` short-circuits when any row in the
  // plan is still actionable.
  const onApproveAll = useCallback(async () => {
    if (bulkOp !== null) return
    if (applyBlockedReason) return
    if (pendingEntries.length === 0) return
    if (pendingEntries.some(({ proposal }) => permissionBlockedReason(proposal) !== null)) return
    const targets = pendingEntries
    setBulkOp({ kind: 'applying', total: targets.length, completed: 0 })
    try {
      for (let i = 0; i < targets.length; i++) {
        const { toolUseId, proposal } = targets[i]
        const outcome = await performApply(toolUseId, proposal)
        setBulkOp({
          kind: 'applying',
          total: targets.length,
          completed: i + 1
        })
        // A plan-wide failure already settled every remaining sibling;
        // re-applying them would just repeat the same refusal.
        if (outcome.kind === 'apply_failed' && outcome.planWide) break
      }
    } finally {
      setBulkOp(null)
    }
  }, [applyBlockedReason, bulkOp, pendingEntries, performApply, permissionBlockedReason])

  const onRejectAll = useCallback(async () => {
    if (bulkOp !== null) return
    if (pendingEntries.length === 0) return
    const targets = pendingEntries
    setBulkOp({ kind: 'rejecting', total: targets.length, completed: 0 })
    try {
      for (let i = 0; i < targets.length; i++) {
        const { toolUseId, proposal } = targets[i]
        await performReject(toolUseId, proposal, BULK_REJECT_REASON)
        setBulkOp({
          kind: 'rejecting',
          total: targets.length,
          completed: i + 1
        })
      }
    } finally {
      setBulkOp(null)
    }
  }, [bulkOp, pendingEntries, performReject])

  const settledEntries = useMemo(
    () => entries.filter((e) => e.proposal.terminalState !== undefined),
    [entries]
  )

  /**
   * Dismiss settled cards. Fully-settled plans get their resume thunk
   * kicked (it removes the plan's cards itself once the follow-up turn is
   * claimed); settled cards of plans that still have undecided siblings,
   * and cards that never got a planId, are removed directly. Dismissed
   * rows of a part-decided plan won't appear in its eventual resume
   * instruction — an explicit dismissal trades that detail for a clean
   * sidebar.
   */
  const onDismissSettled = useCallback(() => {
    const planFullySettled = (planId: string) =>
      entries.every((e) => e.proposal.planId !== planId || e.proposal.terminalState !== undefined)
    const firedPlans = new Set<string>()
    const toRemove: string[] = []
    for (const { toolUseId, proposal } of settledEntries) {
      const planId = proposal.planId
      if (planId && planFullySettled(planId)) {
        if (!firedPlans.has(planId)) {
          firedPlans.add(planId)
          kickPlanResume(planId)
        }
        toRemove.push(toolUseId)
        continue
      }
      toRemove.push(toolUseId)
      if (firePlanResume) {
        dispatch(removeInFlightAgentAction({ id: toolUseId }))
      }
    }
    onRemoveSettled?.(toRemove)
  }, [dispatch, entries, firePlanResume, kickPlanResume, onRemoveSettled, settledEntries])

  const statusLine = useAgentActionStatusLine(proposals, bulkOp, firePlanResume)
  const batchSummary = entries.find((entry) => entry.proposal.batchSummary)?.proposal.batchSummary
  const showBulkButtons = pendingEntries.length >= 2 || bulkOp !== null
  const permissionBlocked = pendingEntries
    .map(({ proposal }) => permissionBlockedReason(proposal))
    .find((reason): reason is string => reason !== null)
  const bulkApplyBlockedReason = applyBlockedReason ?? permissionBlocked ?? null
  const applyBlocked = Boolean(bulkApplyBlockedReason)
  // Small batches expand so the user immediately sees the detail; larger
  // batches start collapsed (compact one-line rows) so the panel stays
  // readable instead of cramming dozens of expanded detail bodies into
  // the capped scroll viewport.
  const defaultOpen = entries.length <= 2

  if (entries.length === 0) return null

  return (
    <div
      className={cn(
        'shrink-0 border-t border-line/70 bg-background/95 px-3 py-3 backdrop-blur-sm duration-300 animate-in fade-in slide-in-from-bottom-1',
        className
      )}
    >
      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-3 shadow-sm">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            <span className="size-1.5 animate-pulse rounded-full bg-primary" />
            <span className="truncate">{statusLine ?? 'Awaiting your decision…'}</span>
          </div>
          {showBulkButtons ? (
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={onRejectAll}
                disabled={bulkOp !== null || pendingEntries.length === 0}
                className="cursor-pointer rounded-full border border-line bg-background/80 px-2.5 py-1 text-10 font-medium text-muted-foreground transition-colors duration-200 ease-out hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                {bulkOp?.kind === 'rejecting'
                  ? 'Rejecting...'
                  : `Reject all (${pendingEntries.length})`}
              </button>
              <button
                type="button"
                onClick={onApproveAll}
                disabled={bulkOp !== null || pendingEntries.length === 0 || applyBlocked}
                title={bulkApplyBlockedReason ?? undefined}
                className="cursor-pointer rounded-full bg-primary px-2.5 py-1 text-10 font-medium text-primary-foreground shadow-sm transition-colors duration-200 ease-out hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {bulkOp?.kind === 'applying'
                  ? 'Approving...'
                  : `Approve all (${pendingEntries.length})`}
              </button>
            </div>
          ) : null}
          {settledEntries.length > 0 && bulkOp === null ? (
            <button
              type="button"
              onClick={onDismissSettled}
              title="Dismiss decided cards"
              className="shrink-0 cursor-pointer rounded-full p-1 text-muted-foreground transition-colors duration-200 ease-out hover:bg-muted hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </div>
        {applyBlockedReason ? (
          <p className="mb-2 text-11 leading-relaxed text-muted-foreground">{applyBlockedReason}</p>
        ) : null}
        {batchSummary ? (
          <p className="mb-2 text-11 leading-relaxed text-muted-foreground">{batchSummary}</p>
        ) : null}
        <div className="flex max-h-[min(55vh,30rem)] flex-col gap-2 overflow-y-auto pr-0.5">
          {entries.map(({ toolUseId, proposal }) => (
            <AgentActionInFlightCard
              key={toolUseId}
              toolUseId={toolUseId}
              proposal={proposal}
              performApply={performApply}
              performReject={performReject}
              bulkOp={bulkOp}
              defaultOpen={defaultOpen}
              applyBlocked={Boolean(applyBlockedReason || permissionBlockedReason(proposal))}
              applyBlockedReason={applyBlockedReason ?? permissionBlockedReason(proposal)}
            />
          ))}
        </div>
      </div>
    </div>
  )
})

function useAgentActionStatusLine(
  proposals: Record<string, InFlightAgentActionProposal>,
  bulkOp: BulkOpState,
  firePlanResume: boolean
): string | null {
  const [nowMs, setNowMs] = useState<number>(() => Date.now())

  useEffect(() => {
    setNowMs(Date.now())
    const id = window.setInterval(() => {
      setNowMs(Date.now())
    }, 1000)
    return () => {
      window.clearInterval(id)
    }
  }, [proposals])

  // Active bulk operation overrides the elapsed-time banner with a
  // progress count so the user can see we're working through their
  // proposals.
  if (bulkOp !== null) {
    const verb = bulkOp.kind === 'applying' ? 'Approving' : 'Rejecting'
    return `${verb} ${bulkOp.completed} of ${bulkOp.total}…`
  }

  const entries = Object.values(proposals)
  if (entries.length === 0) return null
  // Only the still-actionable rows drive the "awaiting your decision"
  // banner. Once the user has clicked Apply / Reject the card flips to
  // a settled badge; counting it here would incorrectly read as "still
  // waiting on you" even though the only thing left is relaying the
  // decisions to the agent. The "Confirming…" interstitial below covers
  // that hand-off window on both rails: on the blocking path it lasts
  // until the stalled propose tool wakes and retires the cards; on the
  // fallback rail it lasts until the synthetic resume turn dispatches.
  const pending = entries.filter((w) => w.terminalState === undefined)
  const settledCount = entries.length - pending.length
  if (pending.length === 0) {
    const failed = entries.filter((w) => w.terminalState?.kind === 'apply_failed')
    if (failed.length > 0) {
      const first = failed[0]?.terminalState
      const detail = first?.kind === 'apply_failed' ? first.error.trim() : ''
      if (failed.length === 1) {
        return detail ? `Apply failed — ${detail}` : 'Apply failed.'
      }
      return `${failed.length} applies failed.`
    }
    if (!firePlanResume) {
      return settledCount === 1 ? 'Confirmed.' : `Confirmed ${settledCount} decisions.`
    }
    if (settledCount === 1) return 'Confirming with the agent…'
    return `Confirming ${settledCount} decisions with the agent…`
  }
  const earliestStart = pending.reduce(
    (min, w) => Math.min(min, w.startedAtMs),
    Number.POSITIVE_INFINITY
  )
  const elapsedSec = Math.max(0, Math.floor((nowMs - earliestStart) / 1000))
  const formatted = formatElapsed(elapsedSec)
  if (pending.length === 1) {
    return `Awaiting your decision… (${formatted})`
  }
  return `Awaiting your decision on ${pending.length} proposals… (${formatted})`
}

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Local UI status for an in-flight (still actionable) proposal card.
 * Once `proposal.terminalState` is set on the slice the card flips to
 * a settled presentation that doesn't use this state at all — see
 * `SettledCardRow` below.
 */
type CardStatus = 'idle' | 'applying' | 'rejecting' | 'failed'

interface CardState {
  status: CardStatus
  error: string | null
}

const IDLE_CARD_STATE: CardState = { status: 'idle', error: null }

const AgentActionInFlightCard = memo(function AgentActionInFlightCard({
  toolUseId,
  proposal,
  performApply,
  performReject,
  bulkOp,
  defaultOpen,
  applyBlocked,
  applyBlockedReason
}: {
  toolUseId: string
  proposal: InFlightAgentActionProposal
  performApply: (
    toolUseId: string,
    proposal: InFlightAgentActionProposal
  ) => Promise<AgentActionApplyOutcome>
  performReject: (
    toolUseId: string,
    proposal: InFlightAgentActionProposal,
    reason: string
  ) => Promise<AgentActionRejectOutcome>
  bulkOp: BulkOpState
  defaultOpen: boolean
  applyBlocked: boolean
  applyBlockedReason: string | null
}) {
  const [open, setOpen] = useState(defaultOpen)
  const [card, setCard] = useState<CardState>(IDLE_CARD_STATE)
  const view = getAgentActionView(proposal.kind)

  // Once the slice has a terminalState the row is durably settled
  // server-side (`agent_writes.state` is `done` or `error`). Hitting
  // /apply or /reject again would 409 with "is in state \"done\"…" and
  // throw out of `.unwrap()`, which used to flip the local card to
  // 'failed' and surface "Retry apply" — that bug is what the
  // !isSettled gate below avoids.
  const isSettled = proposal.terminalState !== undefined
  const bulkInProgress = bulkOp !== null

  const onApply = useCallback(async () => {
    if (isSettled || bulkInProgress || applyBlocked) return
    if (card.status === 'applying' || card.status === 'rejecting') return
    setCard({ status: 'applying', error: null })
    const outcome = await performApply(toolUseId, proposal)
    if (outcome.kind === 'transport_error') {
      setCard({ status: 'failed', error: outcome.error })
    } else {
      // success / apply_failed: terminalState is set on the slice and
      // the card flips to its settled presentation, so reset local
      // state.
      setCard(IDLE_CARD_STATE)
    }
  }, [applyBlocked, bulkInProgress, card.status, isSettled, performApply, proposal, toolUseId])

  const onReject = useCallback(async () => {
    if (isSettled || bulkInProgress) return
    if (card.status === 'applying' || card.status === 'rejecting') return
    setCard({ status: 'rejecting', error: null })
    const outcome = await performReject(toolUseId, proposal, SINGLE_REJECT_REASON)
    if (outcome.kind === 'transport_error') {
      setCard({ status: 'failed', error: outcome.error })
    } else if (outcome.kind === 'reject_failed') {
      // Server-side reject conflict (e.g. row already settled). Keep
      // the card actionable so the user can choose to retry or apply.
      setCard({ status: 'failed', error: outcome.error })
    } else {
      setCard(IDLE_CARD_STATE)
    }
  }, [bulkInProgress, card.status, isSettled, performReject, proposal, toolUseId])

  const inFlight = card.status === 'applying' || card.status === 'rejecting'
  const buttonsDisabled = inFlight || bulkInProgress
  const batchLabel =
    proposal.batchSize !== undefined && proposal.batchSize > 1 && proposal.batchIndex !== undefined
      ? `Proposal ${proposal.batchIndex + 1} of ${proposal.batchSize}`
      : null

  return (
    <div className="group/card flex min-w-0 shrink-0 flex-col overflow-hidden rounded-xl border border-line/80 bg-card shadow-sm transition-[border-color,box-shadow] duration-200 ease-out hover:border-primary/40 hover:shadow-md">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={open ? 'Click to collapse details' : 'Click to expand details'}
        className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2.5 text-left transition-colors duration-200 ease-out hover:bg-muted/50 active:bg-muted/70"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-9 font-semibold tracking-wide text-primary uppercase">
              {view?.badgeLabel(proposal) ?? proposal.kind}
            </span>
            {batchLabel ? (
              <span className="truncate text-10 tracking-wide text-muted-foreground uppercase">
                {batchLabel}
              </span>
            ) : null}
          </div>
          <p
            className={cn(
              'mt-1 break-words text-12 leading-snug text-foreground',
              !open && 'line-clamp-2'
            )}
          >
            {proposal.summary}
          </p>
        </div>
        <CardStatusBadge
          status={card.status}
          terminalState={proposal.terminalState}
          bulkInProgress={bulkInProgress}
        />
        <ChevronDown
          aria-hidden
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out group-hover/card:text-foreground',
            open ? 'rotate-0' : '-rotate-90'
          )}
        />
      </button>
      <div
        data-open={open}
        className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out data-[open=true]:grid-rows-[1fr]"
      >
        <div className="min-h-0 overflow-hidden">
          <div
            data-open={open}
            className="flex flex-col gap-2 border-t border-line/70 p-2 opacity-0 transition-opacity duration-200 ease-out data-[open=true]:opacity-100"
          >
            {view?.renderDetail(proposal) ?? null}
            {!isSettled && card.error ? (
              <p className="px-1 text-10 text-destructive">{card.error}</p>
            ) : null}
            {isSettled ? (view?.renderSettledSummary(proposal) ?? null) : null}
            {!isSettled ? (
              <div className="flex items-center justify-end gap-1.5 px-1">
                <button
                  type="button"
                  onClick={onReject}
                  disabled={buttonsDisabled || !!view?.rejectBlockedReason?.(proposal)}
                  title={view?.rejectBlockedReason?.(proposal) ?? undefined}
                  className="cursor-pointer rounded-full border border-destructive/25 bg-destructive/10 px-2.5 py-1 text-10 font-medium text-destructive transition-colors duration-200 ease-out hover:bg-destructive hover:text-destructive-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {view?.rejectLabel ?? 'Reject'}
                </button>
                <button
                  type="button"
                  onClick={onApply}
                  disabled={buttonsDisabled || applyBlocked}
                  title={applyBlocked ? (applyBlockedReason ?? undefined) : undefined}
                  className="cursor-pointer rounded-full bg-primary px-2.5 py-1 text-10 font-medium text-primary-foreground shadow-sm transition-colors duration-200 ease-out hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {view?.applyButtonLabel?.(proposal, card.status === 'failed') ??
                    (card.status === 'failed' ? 'Retry apply' : 'Apply')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
})

function CardStatusBadge({
  status,
  terminalState,
  bulkInProgress
}: {
  status: CardStatus
  terminalState: InFlightAgentActionProposal['terminalState']
  bulkInProgress: boolean
}) {
  // Slice-driven settlement (terminalState set by the apply/reject
  // mutation success path) takes precedence over the local mutation
  // state. Once a row settles the apply/reject buttons disappear and
  // the badge renders the durable outcome.
  if (terminalState) {
    if (terminalState.kind === 'applied') {
      return (
        <Check
          aria-label="Applied"
          className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400"
        />
      )
    }
    if (terminalState.kind === 'apply_failed') {
      return (
        <AlertTriangle aria-label="Apply failed" className="size-3.5 shrink-0 text-destructive" />
      )
    }
    return <XCircle aria-label="Rejected" className="size-3.5 shrink-0 text-muted-foreground" />
  }
  if (status === 'applying' || status === 'rejecting' || bulkInProgress) {
    const label = status === 'rejecting' ? 'rejecting' : 'applying'
    return (
      <Loader2
        aria-label={bulkInProgress ? 'queued' : label}
        className="size-3.5 shrink-0 animate-spin text-muted-foreground"
      />
    )
  }
  if (status === 'failed') {
    return <AlertTriangle aria-label="Failed" className="size-3.5 shrink-0 text-destructive" />
  }
  return null
}
